import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "components/app/dialog/dialog";
import { Button } from "components/ui/button";
import { ScrollArea, ScrollBar } from "components/ui/scroll-area";
import { Loader, X } from "lucide-react";
import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { AppLog } from "services";
import { useAppSelector } from "store/store";
import { RequestApi, RouteApi, StatusMsg } from "utils";
import StaffAction from "../StaffAction";
import TextInput from "components/app/function/TextInput";
import Religion from "../dummyStaff";
import TimePicker from "components/app/function/TimePicker";
import NumberInput from "components/app/function/NumberInput";

interface DialogProps {
  isUpdateShift: any;
  setIsUpdateShift: any;
  userShopShiftGroup: any;
  setUserShopShiftGroup: any;
  isCreateShift: boolean;
  isDailogAddOrEditShiftGroup: boolean;
  setIsDailogAddOrEditShiftGroup: (value: boolean) => void;
}
export default function DailogAddAndEditShiftGroup({
  userShopShiftGroup,
  setUserShopShiftGroup,
  isUpdateShift,
  setIsUpdateShift,
  isCreateShift,
  isDailogAddOrEditShiftGroup,
  setIsDailogAddOrEditShiftGroup,
}: DialogProps) {
  const { clearDatashift, handleChange, formData, setFormData } = StaffAction();
  const { token } = useAppSelector((state) => state.auth);
  const [loadingSubmitAddOrEditShift, setLoadingSubmitAddOrEditShift] = useState<boolean>(false);
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const shopId = searchParams.get("id");
  const { days } = Religion();
  useEffect(() => {
    if (!isUpdateShift) return; // Ensure update only when needed
    const results = userShopShiftGroup.filter((item: any) => item.id === isUpdateShift);

    if (results.length > 0) {
      const shiftData = results[0]; // Get the first matched shift
      setFormData({
        shift_group_id: shiftData.shift_group_id,
        shop_id: shopId,
        latitude: shiftData.latitude,
        longitude: shiftData.longitude,
        max_distance: shiftData.max_distance,
        local_description: shiftData.local_description,
        working_hours: days.map((day) => {
          const matchedShift = shiftData.working_hours.find((shift: any) => shift.weekday === day);
          return matchedShift
            ? {
                weekday: matchedShift.weekday,
                starting_time: matchedShift.starting_time || "",
                ending_time: matchedShift.ending_time || "",
                no_of_working_hour: matchedShift.no_of_working_hour || "",
                no_of_break_minute: matchedShift.no_of_break_minute || "",
                break_starting_time: matchedShift.break_starting_time || "",
                break_ending_time: matchedShift.break_ending_time || "",
              }
            : {
                weekday: day,
                starting_time: "",
                ending_time: "",
                no_of_working_hour: "",
                no_of_break_minute: "",
                break_starting_time: "",
                break_ending_time: "",
              };
        }),
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isUpdateShift, userShopShiftGroup, shopId]);

  const onSubmitAddOrEditShift = async () => {
    setLoadingSubmitAddOrEditShift(true);
    try {
      let url: string = "";
      url = !isUpdateShift ? RouteApi.submitInsertShopShiftGroup : RouteApi.submitUpdateShopShiftGroup;
      url += `token=${token}`;
      const response = await RequestApi(url, { data: formData });
      if (response.status === "success") {
        setUserShopShiftGroup((prevItemShiftGroup: any[]) => {
          // Ensure prevItemShiftGroup is an array; if not, initialize it as an empty array
          if (!Array.isArray(prevItemShiftGroup)) {
            prevItemShiftGroup = [];
          }
          const shiftData = response.user_shop_shift_group; // Extract shift group data

          // Check if the shift group already exists in the array
          const exists = prevItemShiftGroup.some((item) => item.id === shiftData.id);
          if (exists) {
            // Update existing item
            return prevItemShiftGroup.map((item) => (item.id === shiftData.id ? { ...item, ...shiftData } : item));
          } else {
            // Add new item if it doesn't exist
            return [...prevItemShiftGroup, shiftData];
          }
        });
        clearDatashift();
        setLoadingSubmitAddOrEditShift(false);
        setIsDailogAddOrEditShiftGroup(false);
      } else {
        console.log(response, "error");
      }
    } catch (error) {
      AppLog.show({
        message: `[onSubmitAddOrEditShift]`,
        status: StatusMsg.error,
        error,
      });
      setLoadingSubmitAddOrEditShift(false);
    } finally {
    }
  };
  return (
    <Dialog open={isDailogAddOrEditShiftGroup}>
      <DialogContent isShowBtnXClose={false} className="custom-dialog-class flex flex-col justify-between lg:max-w-[1000px] md:max-w-[800px] h-[calc(100vh-80px)]">
        <DialogHeader>
          <DialogTitle> {isUpdateShift ? " Update Shift Group" : " Create New Shift Group"}</DialogTitle>
          <DialogDescription>Input shift or site description and working hours by weekday, click "Save" when you finished</DialogDescription>
          <div
            className="absolute flex items-center justify-center right-4 top-1 z-40 cursor-pointer rounded-full bg-gray-200 dark:bg-accent w-[28px] h-[28px]"
            onClick={() => {
              clearDatashift();
              setIsUpdateShift(null);
              setIsDailogAddOrEditShiftGroup(false);
            }}
          >
            <X />
          </div>
        </DialogHeader>
        <ScrollArea className="mt-2 w-full gap-3  pr-[20px]">
          <div className="flex flex-col pb-[10px] h-full ">
            <div className="sticky z-50 top-[2px] left-0  dark:bg-[#020817] px-1  bg-white">
              <div className=" grid grid-cols-1 md:grid-cols-4 gap-4">
                <div className="pb-2 ">
                  <label className="block mb-1 text-sm font-medium">Description</label>
                  <TextInput
                    value={formData.local_description}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        local_description: e.target.value,
                      })
                    }
                  />
                </div>
                <div className="pb-2 ">
                  <label className="block mb-1 text-sm font-medium">Latitude</label>
                  <TextInput
                    value={formData.latitude}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        latitude: e.target.value,
                      })
                    }
                  />
                </div>
                <div className="pb-2 ">
                  <label className="block mb-1 text-sm font-medium">Longitude</label>
                  <TextInput
                    value={formData.longitude}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        longitude: e.target.value,
                      })
                    }
                  />
                </div>
                <div className="pb-2 ">
                  <label className="block mb-1 text-sm font-medium">Max. Distance</label>
                  <TextInput
                    value={formData.max_distance}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        max_distance: e.target.value,
                      })
                    }
                  />
                </div>
              </div>
            </div>
            <div className="space-y-4 mt-3">
              {days.map((day, index) => (
                // <div key={day} className="px-2 py-2 mt-2 rounded-md bg-gray-100 dark:bg-[#1e293b]">
                //   <label className="block mb-1 text-md font-medium">{day}</label>
                //   <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                //     <div>
                //       <label className="block mb-1 text-sm font-medium">Starting Time</label>
                //       <TimePicker value={formData.working_hours[index].starting_time} onChange={(val) => handleChange(index, "starting_time", val)} />
                //     </div>
                //     <div>
                //       <label className="block mb-1 text-sm font-medium">Working (Hours)</label>
                //       <NumberInput
                //         className=" text-base  border px-2 py-1 rounded-lg"
                //         value={formData.working_hours[index].no_of_working_hour}
                //         onChange={(e) => handleChange(index, "no_of_working_hour", e.target.value)}
                //       />
                //     </div>
                //     <div>
                //       {/* <label className="block mb-1 text-sm font-medium">Breaking (Minutes)</label> */}
                //       <label className="block mb-1 text-sm font-medium">Start Time (Break)</label>
                //       <NumberInput
                //         className=" text-base  border px-2 py-1 rounded-lg"
                //         value={formData.working_hours[index].no_of_break_minute}
                //         onChange={(e) => handleChange(index, "no_of_break_minute", e.target.value)}
                //       />
                //     </div>
                //     <div>
                //       <label className="block mb-1 text-sm font-medium">End Time (Break)</label>
                //       <TimePicker value={formData.working_hours[index].ending_time} onChange={(val) => handleChange(index, "ending_time", val)} />
                //     </div>
                //   </div>
                // </div>
                <div key={day} className="px-2 py-2 mt-2 rounded-md bg-gray-100 dark:bg-[#1e293b]">
                  <label className="block mb-1 text-md font-medium">{day}</label>
                  <div className="grid grid-cols-1 md:grid-cols-6 gap-4">
                    <div>
                      <label className="block mb-1 text-sm font-medium">Starting Time</label>
                      <TimePicker value={formData.working_hours[index].starting_time} onChange={(val) => handleChange(index, "starting_time", val)} />
                    </div>
                    <div>
                      <label className="block mb-1 text-sm font-medium">Working (Hours)</label>
                      <NumberInput
                        className=" py-[10px] min-h-[44px]"
                        value={formData.working_hours[index].no_of_working_hour}
                        onChange={(e) => handleChange(index, "no_of_working_hour", e.target.value)}
                      />
                    </div>

                    <div>
                      {/* <label className="block mb-1 text-sm font-medium">Breaking (Minutes)</label> */}
                      <label className="block mb-1 text-sm font-medium">Start Time (Break)</label>
                      <TimePicker value={formData.working_hours[index].break_starting_time} onChange={(val) => handleChange(index, "break_starting_time", val)} />
                    </div>
                    <div>
                      <label className="block mb-1 text-sm font-medium">Breaking (Minutes)</label>
                      <NumberInput
                        className="  py-[10px] min-h-[45px]"
                        value={formData.working_hours[index].no_of_break_minute}
                        onChange={(e) => handleChange(index, "no_of_break_minute", e.target.value)}
                      />
                    </div>
                    <div>
                      <label className="block mb-1 text-sm font-medium">End Time (Break)</label>
                      <TimePicker value={formData.working_hours[index].break_ending_time} onChange={(val) => handleChange(index, "break_ending_time", val)} />
                    </div>

                    <div>
                      <label className="block mb-1 text-sm font-medium">Ending Time</label>
                      <TimePicker value={formData.working_hours[index].ending_time} onChange={(val) => handleChange(index, "ending_time", val)} />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
          <ScrollBar orientation="vertical" />
        </ScrollArea>
        <DialogFooter>
          <Button
            onClick={() => {
              onSubmitAddOrEditShift();
            }}
            className="w-full text-white mt-3 bg-blue-600 hover:bg-blue-500"
          >
            {loadingSubmitAddOrEditShift ? <Loader className="animate-spin" /> : "Save"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
